import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getPublicCatalogueExamples,
  type CatalogueExample,
} from "../../api/catalogue";
import { Card, CardFooter, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Collapsible, CollapsibleTrigger } from "../ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  ChevronDown,
  ChevronRight,
  User,
  Eye,
  FileText,
  Store,
} from "lucide-react";
import { PageLoader } from "../ui/page-loader";
import ExamplePreviewDialog from "./ExamplePreviewDialog";
import UserProfileModal from "../marketplace/UserProfileModal";
import ExampleDetailModal from "../marketplace/ExampleDetailModal";
import CompanionBanner from "../companion/CompanionBanner";
import { useCompanion } from "../../context/CompanionProvider";

const TERM_KEYS = ["activity", "engagement", "prologue", "epilogue"] as const;
const TERM_PLACEHOLDER_REGEX =
  /\{\{(activity|engagement|prologue|epilogue)\}\}/g;

function DescriptionWithTooltips({
  description,
  t,
}: {
  description: string;
  t: (key: string) => string;
}) {
  const paragraphs = description.split(/\n\n/).filter(Boolean);
  return (
    <>
      {paragraphs.map((para, i) => (
        <p key={i} className={i > 0 ? "mt-2" : ""}>
          {para.split(TERM_PLACEHOLDER_REGEX).map((part, j) => {
            const termKey = TERM_KEYS.includes(
              part as (typeof TERM_KEYS)[number]
            )
              ? (part as (typeof TERM_KEYS)[number])
              : null;
            if (termKey) {
              return (
                <Tooltip key={j}>
                  <TooltipTrigger asChild>
                    <span className="text-primary cursor-help">
                      {t(`term_${termKey}`)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {t(`tooltip_${termKey}`)}
                  </TooltipContent>
                </Tooltip>
              );
            }
            return <React.Fragment key={j}>{part}</React.Fragment>;
          })}
        </p>
      ))}
    </>
  );
}

const ExampleCatalogue: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { messages: companionMessages, dismissMessage: companionDismiss } = useCompanion();
  const [selectedExample, setSelectedExample] =
    useState<CatalogueExample | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedExampleDetail, setSelectedExampleDetail] =
    useState<CatalogueExample | null>(null);
  const [whatIsExampleOpen, setWhatIsExampleOpen] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<CatalogueExample["creator"]>(null);

  const {
    data: examples = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["publicCatalogueExamples"],
    queryFn: getPublicCatalogueExamples,
  });

  const openDetailModal = (example: CatalogueExample) => {
    setSelectedExampleDetail(example);
  };

  const openPreviewFromDetail = () => {
    if (selectedExampleDetail) {
      setSelectedExample(selectedExampleDetail);
      setPreviewOpen(true);
      setSelectedExampleDetail(null);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setSelectedExample(null);
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {t("example_catalogue") || "Example Catalogue"}
        </h1>
        <p className="mt-1 text-gray-600">
          {t("example_catalogue_intro") ||
            "Real journeys through our workflows. See how others started, what they did, and what changed."}
        </p>
      </div>

      <Collapsible
        open={whatIsExampleOpen}
        onOpenChange={setWhatIsExampleOpen}
        className="mb-6"
      >
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-left text-sm font-medium text-gray-600 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-900">
          {whatIsExampleOpen ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          {t("what_is_an_example") || "What is an example?"}
        </CollapsibleTrigger>
        {/* Animated wrapper: content stays in DOM to avoid layout shift; height transitions smoothly */}
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{
            gridTemplateRows: whatIsExampleOpen ? "1fr" : "0fr",
          }}
        >
          <div className="overflow-hidden min-h-0">
            <TooltipProvider>
              <div className="mt-2 pl-1 text-sm text-gray-600 max-w-xl md:pl-4">
                <DescriptionWithTooltips
                  description={
                    t("what_is_an_example_description") ||
                    "Examples show real engagements with an activity on ClaryNext. They let you follow one person's journey — where they started (the prologue), what they experienced during the engagement, and what changed or what they learned in the end (the epilogue).\n\nBy following an example, you see the activity in action through real experience.\n\nThis helps you imagine yourself in a similar situation, understand how someone connected the activity to their real life, and learn how meaningful change can happen through true engagement.\n\nExamples are here to help you engage more deeply when you try an activity yourself."
                  }
                  t={t}
                />
              </div>
            </TooltipProvider>
          </div>
        </div>
      </Collapsible>

      {isLoading && (
        <PageLoader />
      )}
      {error && (
        <div className="rounded-lg border border-red-100 bg-red-50/80 px-4 py-3">
          <p className="text-sm text-red-700">
            {t("example_catalogue_error") ||
              "Could not load examples. Please try again later."}
          </p>
        </div>
      )}
      {/* Companion Banner */}
      {companionMessages.map((msg) => (
        <div key={msg.id} className="mb-6">
          <CompanionBanner message={msg} onDismiss={companionDismiss} />
        </div>
      ))}

      {!isLoading && !error && examples.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <p className="font-medium text-gray-600">
            {t("example_catalogue_empty") ||
              "No examples are available right now."}
          </p>
        </div>
      )}

      {!isLoading && !error && examples.length > 0 && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {examples.map((example) => (
            <Card
              key={example._id}
              className="flex flex-col overflow-hidden border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-lg"
            >
              <CardHeader className="p-5 pb-3">
                <h3 className="text-lg font-semibold leading-tight tracking-tight text-gray-900">
                  {example.name}
                </h3>
                <div className="flex items-center gap-1.5 pt-2 text-gray-500">
                  <User className="h-3.5 w-3.5 flex-shrink-0" />
                  {example.creator ? (
                    example.creator.name === "Anonymous" ? (
                      <span className="text-xs">
                        {t("anonymous") || "Anonymous"}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUser(example.creator ?? null);
                        }}
                        className="text-left text-xs text-primary hover:underline"
                      >
                        {example.creator.name}
                      </button>
                    )
                  ) : (
                    <span className="text-xs">—</span>
                  )}
                </div>
              </CardHeader>
              <CardFooter className="mt-auto flex flex-col gap-3 border-t border-gray-100 bg-gray-50 p-4 pt-4">
                <div className="flex w-full gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedExample(example);
                      setPreviewOpen(true);
                    }}
                    className="flex-1 gap-1.5"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {t("view") || "View"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDetailModal(example)}
                    className="flex-1 gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {t("details") || "Details"}
                  </Button>
                </div>
                {example.marketplaceEntityId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5 border-gray-200 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700"
                    onClick={() =>
                      navigate(
                        `/marketplace/product/${example.marketplaceEntityId}`
                      )
                    }
                  >
                    <Store className="h-3.5 w-3.5" />
                    {t("view_in_marketplace") || "View Activity in Marketplace"}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {selectedExample && (
        <ExamplePreviewDialog
          example={{
            _id: selectedExample._id,
            name: selectedExample.name,
          }}
          activityId={selectedExample.fiPrescription}
          open={previewOpen}
          onOpenChange={(open) => !open && closePreview()}
          isEditMode={false}
          marketplaceEntityId={selectedExample.marketplaceEntityId ?? undefined}
        />
      )}

      <ExampleDetailModal
        open={selectedExampleDetail !== null}
        onOpenChange={(open) => !open && setSelectedExampleDetail(null)}
        example={
          selectedExampleDetail
            ? {
                name: selectedExampleDetail.name,
                activityName:
                  selectedExampleDetail.prescriptionName || undefined,
                description: selectedExampleDetail.description,
                prologue: selectedExampleDetail.prologue,
                epilogue: selectedExampleDetail.epilogue,
              }
            : null
        }
        onViewClick={openPreviewFromDetail}
      />

      <UserProfileModal
        open={selectedUser !== null && selectedUser?.name !== "Anonymous"}
        onOpenChange={(open) => !open && setSelectedUser(null)}
        user={
          selectedUser && selectedUser.name !== "Anonymous"
            ? selectedUser
            : null
        }
      />
    </div>
  );
};

export default ExampleCatalogue;
